import { KeyboardArrowDown, MoreVert } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import Clear from '@mui/icons-material/Clear';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
  AlertColor,
  Box,
  Button,
  ButtonGroup,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Checkbox,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import axios from 'axios';
import DrawerMenu from 'components/DrawerMenu';
import Notification from 'components/Notification';
import TablePaginationAction from 'components/TablePaginationAction';
import dayjs from 'dayjs';
import { FC, Fragment, RefObject, useCallback, useMemo, useRef, useState, MouseEvent, useEffect, ReactNode } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Config from 'utils/Config';
import { identifiers } from 'utils/constants/identifiers';
import useWindowSize from 'utils/hooks/useWindowSize';
import {
  colSort,
  filterData,
  filterDataforApi,
  pageNo,
  rowCount,
  setColSort,
  setFilter,
  setFilterDataForApi,
  setPageNo,
  setRowCount,
} from 'utils/redux/reducer/filter-slice';
import { fetchFromStorage } from 'utils/storage';
import { downloadExportFile, getDrawerWidth } from 'utils/validators/HelperFunctions';
import strings from '../../../common/Translation/Translate';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import Form, { FormDataModel } from '../../../components/Form';
import PageLoader from '../../../components/PageLoader';
import { ToolTip } from '../../../components/Tooltip/ToolTip';
import { images } from '../../../utils/constants/images';
import { Privilege } from '../../../utils/redux/reducer/authentication-slice';
import { removeErrorFieldsFromValues } from '../../../utils/validators';
import Delete from './Delete';
import ListPageImportDrawerComponent from './ListPageImportDrawerComponent';
import './styles.scss';
import { pageSubtitleSx, pageTitleSxResponsive } from 'utils/styles/pageHeader';
import { getTableAvatarSx } from 'utils/avatarColors';

const getInitialsFromName = (name?: string | null) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const TableImageCell: FC<{
  imageUrl?: string | null;
  placeholderIcon?: React.ReactNode;
  placeholderInitials?: string;
  placeholderName?: string | null;
}> = ({ imageUrl, placeholderIcon, placeholderInitials, placeholderName }) => {
  const [failed, setFailed] = useState(false);
  const src = imageUrl ? `${Config.BaseURL}${imageUrl}` : null;
  const avatarSx = placeholderInitials
    ? getTableAvatarSx(placeholderName || placeholderInitials, 32)
    : null;

  if (!src || failed) {
    return (
      <Box
        sx={
          avatarSx
            ? {
                ...avatarSx,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }
            : {
                width: 32,
                height: 32,
                bgcolor: 'grey.100',
                color: 'grey.600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                boxShadow: 'none',
                flexShrink: 0,
              }
        }>
        {placeholderInitials ? (
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1, letterSpacing: 0.2 }}>
            {placeholderInitials}
          </Typography>
        ) : (
          placeholderIcon
        )}
      </Box>
    );
  }

  return (
    <img src={src} alt="" height="30px" width="30px" onError={() => setFailed(true)} />
  );
};

export interface PageCustomAction {
  imageUrl?: string;
  materialIcon?: React.ReactNode;
  buttonText?: string;
  privilege: string;
  tooltip?: string;
  handleCustomImageMethod: (id: number, event?: MouseEvent<HTMLElement>) => void;
}

export interface PageConfigModel {
  title: string;
  subtitle?: string;
  /** Plural entity name for pagination (e.g. "RFQs"). Falls back to title, then "records". */
  entityLabel?: string;
  searchField?: string;
  searchPlaceholder?: string;
  /** Prefill the common search bar (e.g. from URL query params). */
  initialSearchText?: string;
  AddButtonText?: string;
  AddButtonUrl?: any;
  hideActionText?: boolean;
  editButtonUrl?: string;
  listApi?: string;
  deleteApi?: any | null;
  editPrivilege?: string;
  deletePrivilege?: string;
  blockUserPrivilege?: string;
  addPrivilege?: string | null;
  sortValue?: { sortOrder?: number; sortBy?: string };
  tableColumn: any[];
  dropdownData?: any[];
  customImage?: PageCustomAction | PageCustomAction[];
  sortHandler?: (sort: string) => void;
  toggleUser?: (id: number) => void;
  filterForm?: any;
  customEdit?: any;
  customAdd?: any;
  customComponent?: any;
  /** Rendered left of / next to the search bar when searchField is set. */
  headerFilters?: () => ReactNode;
  /** When true, search and header filters share the filter bar equally (50/50). */
  equalSearchFilterSplit?: boolean;
  /** Active header/local filters shown as chips under the toolbar (search chip is added automatically). */
  filterChips?: { key: string; label: string }[];
  /** Clear a single chip from `filterChips` (search is cleared automatically by ListPage). */
  onClearFilterChip?: (key: string) => void;
  /** Clear all `filterChips` local state (search/redux cleared by ListPage). */
  onClearAllFilterChips?: () => void;
  /** KPI / stats row rendered under the title + subtitle (above search/toolbar). */
  renderKpis?: () => ReactNode;
  exportApi?: string;
  exportPrivilege?: string;
  /** Override ListPage POST export (e.g. GET blob download). Used when `exportIconOnly` is set. */
  customExport?: () => void | Promise<void>;
  /** Render export as a labeled secondary button (with icon + Export text) instead of Actions menu. */
  exportIconOnly?: boolean;
  /** Render import as a labeled secondary button (with icon + Import text) in the title row. */
  importIconOnly?: boolean;
  importPrivilege?: string;
  importApi?: string;
  hideDefaultEditAction?: boolean;
  hideDefaultDeleteAction?: boolean;
  downloadImportTemplate?: string;
  actionMenuItems?: PageCustomAction | PageCustomAction[];
  /** When true, custom actions render as icon buttons beside the MoreVert menu instead of inside it. */
  inlineCustomActions?: boolean;
  onRowClick?: (item: any) => void;
  /** When set with isRowExpanded, renders accordion detail under the row. */
  renderExpandedRow?: (item: any) => ReactNode;
  isRowExpanded?: (item: any) => boolean;
  headerActionsComponent?: () => ReactNode;
  toolbarComponent?: () => ReactNode;
  enableRowSelection?: boolean;
  selectionToolbar?: (selectedIds: number[], clearSelection: () => void) => ReactNode;
}

export interface ListPageProps {
  pageConfig: PageConfigModel;
  pageResponse: any;
  getList: any;
  isLoading?: boolean;
  isWhiteSpace?: boolean;
  page?: string;
  hideTable?: boolean;
}

const ListPage: FC<ListPageProps> = ({
  pageConfig,
  pageResponse,
  getList,
  isLoading = false,
  isWhiteSpace = true,
  page = '',
  hideTable = false,
}) => {
  const sort = useSelector(colSort);
  const { id } = useParams();
  const Navigate = useNavigate();
  const mobile = useWindowSize();
  let filterRef: RefObject<Form | null | undefined> = useRef();
  const theme = useTheme();
  const dispatch = useDispatch();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [openFilterDrawer, setOpenFilterDrawer] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const IsPrivilege = useSelector(Privilege);
  const [openModal, setOpenModal] = useState(false);
  const [selectedId, setSelectedId] = useState<string>('');
  const [selectedEntityName, setSelectedEntityName] = useState<string>('');
  const index = useSelector(pageNo);
  const filter = useSelector(filterData);
  const apiFilterForSearch = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const [savedFilter, setSavedFilter] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [anchorEl2, setAnchorEl2] = useState<HTMLButtonElement | null>(null);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const [actionMenu, setActionMenu] = useState<{ anchorEl: HTMLElement | null; row: any | null }>({
    anchorEl: null,
    row: null,
  });
  const [searchText, setSearchText] = useState(pageConfig.initialSearchText || '');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seededSearchRef = useRef(pageConfig.initialSearchText || '');

  useEffect(() => {
    const next = pageConfig.initialSearchText || '';
    if (next !== seededSearchRef.current) {
      seededSearchRef.current = next;
      setSearchText(next);
    }
  }, [pageConfig.initialSearchText]);

  const apiFilterRef = useRef(apiFilterForSearch);
  const filterRefState = useRef(filter);
  const pageIndexRef = useRef(index);
  apiFilterRef.current = apiFilterForSearch;
  filterRefState.current = filter;
  pageIndexRef.current = index;
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const actionMenuConfig = pageConfig?.actionMenuItems ?? pageConfig?.customImage;
  const customActions = useMemo(() => {
    if (!actionMenuConfig) return [];
    const list = Array.isArray(actionMenuConfig) ? actionMenuConfig : [actionMenuConfig];
    return list.filter(action => !action?.privilege || IsPrivilege?.includes(action.privilege));
  }, [IsPrivilege, actionMenuConfig]);

  const getRowId = useCallback(
    (row: any) =>
      row?.id ?? row?.Id ?? row?.ID ?? row?._id ?? row?._ID ?? row?.uuid ?? row?.UUID ?? row?.key ?? id ?? '',
    [id],
  );

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const pageRowIds = useMemo(
    () => (pageResponse?.content || []).map((row: any) => Number(getRowId(row))).filter((rowId: number) => !Number.isNaN(rowId)),
    [pageResponse?.content, getRowId],
  );

  const allPageSelected = pageRowIds.length > 0 && pageRowIds.every((rowId: number) => selectedIds.includes(rowId));
  const somePageSelected = pageRowIds.some((rowId: number) => selectedIds.includes(rowId));

  useEffect(() => {
    clearSelection();
  }, [index, pageSize, filter, pageConfig.searchField, clearSelection]);

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedIds(prev => prev.filter(idValue => !pageRowIds.includes(idValue)));
      return;
    }
    setSelectedIds(prev => Array.from(new Set([...prev, ...pageRowIds])));
  };

  const toggleSelectRow = (rowId: number) => {
    setSelectedIds(prev => (prev.includes(rowId) ? prev.filter(idValue => idValue !== rowId) : [...prev, rowId]));
  };

  const handleOpenActionMenu = (event: MouseEvent<HTMLElement>, row: any) => {
    setActionMenu({ anchorEl: event.currentTarget, row });
  };

  const handleCloseActionMenu = () => {
    setActionMenu({ anchorEl: null, row: null });
  };

  useEffect(() => {
    if (!isLoading && pageResponse?.totalElements > 0 && pageResponse?.content?.length === 0 && index > 0) {
      dispatch(setPageNo(0));
    }
  }, [pageResponse?.content?.length, pageResponse?.totalElements, index, dispatch, isLoading]);

  const navigateToEditRow = useCallback(
    (row: any) => {
      const rowId = getRowId(row);
      if (!rowId) return;
      if (pageConfig?.customEdit) {
        pageConfig.customEdit(rowId);
        return;
      }
      if (pageConfig?.editButtonUrl) {
        Navigate(`${pageConfig.editButtonUrl}/${rowId}`);
      }
    },
    [pageConfig?.editButtonUrl, pageConfig?.customEdit, Navigate, getRowId],
  );

  const handleEditRow = () => {
    if (!actionMenu.row) return;
    if (actionMenu.row?.hideEdit) {
      handleCloseActionMenu();
      return;
    }
    handleCloseActionMenu();
    // Check if customEdit is provided, if so use it instead of navigation
    if (pageConfig?.customEdit) {
      const rowId = getRowId(actionMenu.row);
      if (rowId) {
        pageConfig.customEdit(rowId);
      }
    } else {
      navigateToEditRow(actionMenu.row);
    }
  };

  const handleDeleteRow = () => {
    if (!actionMenu.row || actionMenu.row?.hideDelete) {
      handleCloseActionMenu();
      return;
    }
    const rowId = getRowId(actionMenu.row);
    const firstTextCol = pageConfig.tableColumn?.find((c: any) => !c.image);
    const entityName =
      (firstTextCol?.field && actionMenu.row?.[firstTextCol.field]) ||
      actionMenu.row?.name ||
      '';
    handleCloseActionMenu();
    if (rowId) {
      deleteUser(String(rowId), String(entityName || ''));
    }
  };

  const handleToggleRow = () => {
    if (!actionMenu.row || !pageConfig?.toggleUser) {
      handleCloseActionMenu();
      return;
    }
    const rowId = getRowId(actionMenu.row);
    const resolvedId = actionMenu.row?.id ?? rowId;
    handleCloseActionMenu();
    if (resolvedId) {
      pageConfig.toggleUser(resolvedId as number);
    }
  };

  const canEdit = Boolean(
    pageConfig?.editPrivilege && IsPrivilege?.includes(pageConfig.editPrivilege) && !pageConfig.hideDefaultEditAction,
  );
  const canDelete = Boolean(
    pageConfig?.deletePrivilege &&
      IsPrivilege?.includes(pageConfig.deletePrivilege) &&
      !pageConfig.hideDefaultDeleteAction,
  );
  const canToggle = Boolean(
    pageConfig?.blockUserPrivilege && IsPrivilege?.includes(pageConfig.blockUserPrivilege) && pageConfig.toggleUser,
  );
  const hasCustomActions = customActions.length > 0;

  const handleCustomActionClick = (action: PageCustomAction, event: MouseEvent<HTMLElement>) => {
    if (!actionMenu.row) return;
    const rowId = actionMenu.row?.id ?? getRowId(actionMenu.row);
    handleCloseActionMenu();
    if (rowId !== undefined && rowId !== null) {
      action.handleCustomImageMethod(rowId as number, event);
    }
  };

  const getCustomActionLabel = (action: PageCustomAction) => action.buttonText || action.tooltip || strings.action_text;

  const renderCustomActionIcon = (action: PageCustomAction) => {
    if (action.materialIcon) {
      return action.materialIcon;
    }
    if (action.imageUrl) {
      return <Box component="img" src={action.imageUrl} alt="custom action icon" sx={{ width: 18, height: 18 }} />;
    }
    return <MoreVert fontSize="small" />;
  };

  const importDrawerOpen = () => {
    setOpenDrawer(true);
  };
  const importDrawerClose = () => {
    setOpenDrawer(false);
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };
  const deleteUser = async (id: string, entityName = '') => {
    setSelectedId(id);
    setSelectedEntityName(entityName);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setSelectedEntityName('');
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPageNo(newPage));
  };

  const pageSizeChange = (event: any) => {
    const value = event.target.value;
    dispatch(setPageNo(0));
    dispatch(setRowCount(value));
  };
  const openPopOver = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl2(event.currentTarget);
  };
  const openPop = Boolean(anchorEl2);

  const handleExport = () => {
    if (pageConfig.customExport) {
      void pageConfig.customExport();
      return;
    }
    const token = fetchFromStorage(identifiers.access_token);
    setLoading(true);
    axios({
      url: `${Config.BaseURL + (pageConfig?.exportApi || '')}`,
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
    })
      .then(response => {
        setLoading(false);
        downloadExportFile(response, 'xlsx', pageConfig.title);
      })
      .catch(err => {
        setLoading(false);
        setMessage({
          display: true,
          severity: 'error',
          message: err.message || strings.something_went_wrong,
        });
      });
  };

  const deleteModal = useMemo(() => {
    return (
      <Delete
        page={page}
        updateList={() => {
          getList({
            page: index + 1,
            size: pageSize,
            // filter:||[],
            filter: [],
            // eslint-disable-next-line no-useless-concat
            sort: pageConfig?.sortValue?.sortBy
              ? [`${pageConfig?.sortValue?.sortBy as any}: ${pageConfig?.sortValue?.sortOrder}`]
              : ['createdAt:desc'],
          });
          dispatch(setFilter([]));
        }}
        closeModal={closeModal}
        openModal={openModal}
        entityName={selectedEntityName}
        deleteUrl={
          pageConfig.deleteApi && id === undefined
            ? pageConfig.deleteApi(selectedId)
            : pageConfig.deleteApi && pageConfig.deleteApi(selectedId || id, selectedId)
        }
      />
    );
  }, [filter, getList, index, openModal, pageConfig, pageSize, selectedId, selectedEntityName]);

  const handleFilter = () => {
    setOpenFilterDrawer(true);
  };

  const importDrawerCloseFilter = () => {
    setOpenFilterDrawer(false);
  };

  const applyFilter = useCallback(
    async (reset: boolean, e: any) => {
      e.preventDefault();
      const filters: any = [];
      if (reset) {
        dispatch(setFilter(null));
        dispatch(setFilterDataForApi([]));
        setSearchText('');
        if (filterRef.current) {
          const { resetForm } = filterRef.current as { resetForm?: () => void };
          resetForm?.();
        }
      } else {
        const { getFormData } = filterRef.current as {
          getFormData: () => { formData: FormDataModel; isFormValid: boolean };
        };
        const { formData } = getFormData();
        let filter_data = removeErrorFieldsFromValues(formData);
        if (filter_data.startDate) {
          if (dayjs(filter_data.startDate).isValid()) {
            filter_data = { ...filter_data, startDate: dayjs(filter_data.startDate).format('YYYY-MM-DD') };
          }
          if (filter_data.startDate !== '' && !dayjs(filter_data.startDate).isValid()) {
            return;
          }
        }
        if (filter_data.endDate) {
          if (dayjs(filter_data.endDate).isValid()) {
            filter_data = { ...filter_data, endDate: dayjs(filter_data.endDate).format('YYYY-MM-DD') };
          }
          if (filter_data.endDate !== '' && !dayjs(filter_data.endDate).isValid()) {
            return;
          }
        }
        dispatch(setFilter(filter_data));
        Object.keys(filter_data).forEach(key => {
          if (!!filter_data[key]) {
            if (filters.length > 0) {
              filters.push('and');
            }
            const dataValue = filter_data[key]?.id ? filter_data[key]?.id : filter_data[key];
            filters.push(filter_data[key] ? `${key}:cic:${dataValue}` : null);
          }
        });
      }
      const newFilter = filters?.filter((item: any) => item !== null);
      setSavedFilter(newFilter);
      dispatch(setPageNo(0));
      dispatch(setFilterDataForApi(newFilter));
      if (!reset) {
        importDrawerCloseFilter();
      }
    },
    [pageConfig?.sortValue?.sortBy, pageConfig?.sortValue?.sortOrder, pageSize],
  );

  const handleSort = async (localField: any, field: any, direction: any) => {
    const column = localField ? localField : field;
    dispatch(setColSort(!direction));
    pageConfig?.sortHandler && pageConfig?.sortHandler(column?.field);
    const dir = direction ? 'desc' : 'asc';
    const body = {
      page: 1,
      size: pageSize,
      filter: savedFilter,
      sort: column ? [`${column}:${dir}`] : ['createdAt:desc'],
    };
    await getList(body);
  };

  const filterDrawerContent = useMemo(() => {
    return (
      <Box>
        <Grid container>
          <form className="w-100" onSubmit={(e: any) => applyFilter(false, e)}>
            <Grid container item xs={12} spacing={2} alignItems="flex-start">
              <Form ref={filterRef as RefObject<Form>} model={pageConfig.filterForm} values={filter} />
              <Grid item xs={12}>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={(e: any) => {
                      applyFilter(true, e);
                      importDrawerCloseFilter();
                    }}
                    sx={{ flex: 1, textTransform: 'capitalize' }}>
                    {screenSizeUpMd ? strings.clear_filter_text : strings.clearText}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ flex: 1, textTransform: 'capitalize' }}
                    data-testid="applyFilter"
                    onClick={(e: any) => applyFilter(false, e)}>
                    {strings.continue_text}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Box>
    );
  }, [screenSizeUpMd, pageConfig.filterForm, filter, applyFilter, importDrawerCloseFilter]);

  const showFilterButton = Boolean(pageConfig.filterForm) && !pageConfig.searchField;

  useEffect(() => {
    if (!pageConfig.searchField) {
      return;
    }
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      const trimmed = searchText.trim();
      const currentApiFilter = apiFilterRef.current;
      const currentFilter = filterRefState.current;
      const currentIndex = pageIndexRef.current;

      if (!trimmed) {
        const hasApiFilter = Array.isArray(currentApiFilter) && currentApiFilter.length > 0;
        const hasUiFilter =
          currentFilter != null &&
          typeof currentFilter === 'object' &&
          !Array.isArray(currentFilter) &&
          Object.keys(currentFilter).length > 0;
        const hasArrayUiFilter = Array.isArray(currentFilter) && currentFilter.length > 0;
        if (!hasApiFilter && !hasUiFilter && !hasArrayUiFilter && currentIndex === 0) {
          return;
        }
        if (currentIndex !== 0) {
          dispatch(setPageNo(0));
        }
        if (hasUiFilter || hasArrayUiFilter) {
          dispatch(setFilter(null));
        }
        if (hasApiFilter) {
          dispatch(setFilterDataForApi([]));
        }
        setSavedFilter([]);
        return;
      }
      const apiFilter = [`${pageConfig.searchField}:cic:${trimmed}`];
      const sameApiFilter =
        Array.isArray(currentApiFilter) &&
        currentApiFilter.length === 1 &&
        currentApiFilter[0] === apiFilter[0];
      if (sameApiFilter && currentIndex === 0) {
        return;
      }
      dispatch(setPageNo(0));
      dispatch(setFilter({ [pageConfig.searchField as string]: trimmed }));
      dispatch(setFilterDataForApi(apiFilter));
      setSavedFilter(apiFilter);
    }, 350);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [dispatch, pageConfig.searchField, searchText]);

  const headerWidth = () => {
    if ((showFilterButton || pageConfig.searchField) && IsPrivilege?.includes(pageConfig.addPrivilege)) return 5;
    if (showFilterButton || pageConfig.searchField || IsPrivilege?.includes(pageConfig.addPrivilege)) return 7;
    return 12;
  };

  const handleClearFilter = async (key: any) => {
    if (pageConfig.searchField && key === pageConfig.searchField) {
      setSearchText('');
      dispatch(setFilter(null));
      dispatch(setFilterDataForApi([]));
      setSavedFilter([]);
      dispatch(setPageNo(0));
      return;
    }
    if (pageConfig.filterChips?.some(chip => chip.key === key)) {
      pageConfig.onClearFilterChip?.(key);
      return;
    }
    const filters: any = [];
    const filter_data = { ...filter, [key]: '' };
    dispatch(setFilter(filter_data));
    Object.keys(filter_data).forEach(key => {
      if (!!filter_data[key]) {
        if (filters.length > 0) {
          filters.push('and');
        }
        const dataValue = filter_data[key]?.id ? filter_data[key]?.id : filter_data[key];
        filters.push(filter_data[key] ? `${key}:cic:${dataValue}` : null);
      }
    });

    const newFilter = filters?.filter((item: any) => item !== null);
    setSavedFilter(newFilter);
    dispatch(setPageNo(0));
    dispatch(setFilterDataForApi(newFilter));
  };

  const clearAllSearchToolbarFilters = useCallback(() => {
    if (pageConfig.searchField) {
      setSearchText('');
      dispatch(setFilter(null));
      dispatch(setFilterDataForApi([]));
      setSavedFilter([]);
    }
    dispatch(setPageNo(0));
    pageConfig.onClearAllFilterChips?.();
  }, [dispatch, pageConfig]);

  const filterChipSx = {
    textTransform: 'capitalize' as const,
    backgroundColor: '#E3F2FD',
    color: theme.palette.info.dark || '#01579B',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 600,
    height: 24,
    '& .MuiChip-label': {
      px: 1,
      fontSize: '0.7rem',
    },
    '& .MuiChip-deleteIcon': {
      color: theme.palette.info.dark || '#01579B',
      fontSize: 16,
      opacity: 0.85,
      '&:hover': { color: theme.palette.info.dark || '#01579B', opacity: 1 },
    },
  };

  const searchToolbarChips = useMemo(() => {
    if (!pageConfig.searchField && !pageConfig.headerFilters) return [];
    const chips: { key: string; label: string }[] = [];
    const trimmed = searchText.trim();
    if (pageConfig.searchField && trimmed) {
      chips.push({ key: pageConfig.searchField, label: `Search : ${trimmed}` });
    }
    (pageConfig.filterChips || []).forEach(chip => {
      if (chip.label) chips.push(chip);
    });
    return chips;
  }, [pageConfig.filterChips, pageConfig.headerFilters, pageConfig.searchField, searchText]);

  const useModernHeaderToolbar = Boolean(pageConfig.searchField || pageConfig.headerFilters);
  return (
    <>
      {pageResponse?.response?.data?.status === 403 && (
        <Notification
          isOpen={true}
          message={pageResponse?.response?.data?.message}
          severity={'error' as AlertColor}
          closeNotification={() => {}}
        />
      )}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Container
        maxWidth={false}
        sx={{
          '&.MuiContainer-root': {
            pl: 0,
            pr: 0,
          },
        }}>
        <Grid container spacing={1} alignItems="center">
          {useModernHeaderToolbar ? (
            <>
              <Grid
                item
                xs={12}
                container
                spacing={1}
                alignItems="flex-start"
                justifyContent="space-between"
                wrap="nowrap">
                <Grid item xs sx={{ minWidth: 0, pr: 1 }}>
                  <Typography sx={pageTitleSxResponsive(screenSizeDownSm)}>
                    {pageConfig.title}
                  </Typography>
                  {pageConfig.subtitle ? (
                    <Typography sx={{ ...pageSubtitleSx, mt: 0.25 }}>{pageConfig.subtitle}</Typography>
                  ) : null}
                </Grid>
                <Grid item xs="auto" sx={{ flexShrink: 0, ml: 'auto' }}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
                    {pageConfig.customComponent ? pageConfig.customComponent() : null}
                    {pageConfig.exportIconOnly &&
                    pageConfig.exportPrivilege &&
                    IsPrivilege?.includes(pageConfig.exportPrivilege) ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleExport}
                        startIcon={<FileUploadOutlinedIcon fontSize="small" />}
                        sx={{
                          width: 'auto',
                          minHeight: 36,
                          height: 36,
                          textTransform: 'capitalize',
                          px: 1.5,
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          '&:hover': {
                            color: theme.palette.primary.dark,
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: `${theme.palette.primary.main}14`,
                          },
                        }}>
                        {strings.customermgmt_button_export || 'Export'}
                      </Button>
                    ) : null}
                    {(pageConfig.importIconOnly || pageConfig.exportIconOnly) &&
                    pageConfig.importPrivilege &&
                    IsPrivilege?.includes(pageConfig.importPrivilege) ? (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={importDrawerOpen}
                        startIcon={<FileUploadOutlinedIcon fontSize="small" />}
                        sx={{
                          width: 'auto',
                          minHeight: 36,
                          height: 36,
                          textTransform: 'capitalize',
                          px: 1.5,
                          color: theme.palette.primary.main,
                          borderColor: theme.palette.primary.main,
                          '&:hover': {
                            color: theme.palette.primary.dark,
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: `${theme.palette.primary.main}14`,
                          },
                        }}>
                        {strings.usermgmt_button_import || 'Import'}
                      </Button>
                    ) : null}
                    {!pageConfig.exportIconOnly &&
                      !pageConfig.importIconOnly &&
                      (pageConfig?.importPrivilege || pageConfig?.exportPrivilege) && (
                      <Button
                        variant="outlined"
                        onClick={e => {
                          if (pageConfig?.importPrivilege && !pageConfig?.exportPrivilege) {
                            importDrawerOpen();
                            return;
                          }
                          openPopOver(e);
                        }}
                        aria-label={
                          pageConfig?.importPrivilege && !pageConfig?.exportPrivilege
                            ? `${strings.usermgmt_button_import} ${pageConfig?.title || ''}`
                            : strings.action_text
                        }
                        title={
                          pageConfig?.importPrivilege && !pageConfig?.exportPrivilege
                            ? `${strings.usermgmt_button_import} ${pageConfig?.title || ''}`
                            : strings.action_text
                        }
                        sx={{ minWidth: 36, width: 36, height: 36, px: 0 }}>
                        {pageConfig?.importPrivilege && !pageConfig?.exportPrivilege ? (
                          <FileUploadOutlinedIcon color="primary" fontSize="small" />
                        ) : (
                          <MoreVert color="primary" />
                        )}
                      </Button>
                    )}
                    {IsPrivilege?.includes(pageConfig.addPrivilege) && (
                      screenSizeDownSm ? (
                        <PrimaryButton
                          variant="contained"
                          fullWidth={false}
                          onClick={() => {
                            pageConfig.customAdd ? pageConfig.customAdd() : Navigate(pageConfig.AddButtonUrl);
                          }}
                          sx={{ minWidth: 36, width: 36, height: 36, px: 0 }}>
                          <AddIcon fontSize="small" />
                        </PrimaryButton>
                      ) : (
                        <PrimaryButton
                          variant="contained"
                          data-testid="add"
                          fullWidth={false}
                          onClick={() => {
                            pageConfig.customAdd ? pageConfig.customAdd() : Navigate(pageConfig.AddButtonUrl);
                          }}
                          sx={{ width: 'auto', minHeight: 36, height: 36, textTransform: 'capitalize', px: 1.5 }}>
                          <AddIcon fontSize="small" />
                          {pageConfig.AddButtonText}
                        </PrimaryButton>
                      )
                    )}
                  </Stack>
                </Grid>
              </Grid>
              {pageConfig.renderKpis ? (
                <Grid item xs={12} sx={{ mt: 0.5 }}>
                  {pageConfig.renderKpis()}
                </Grid>
              ) : null}
              <Grid item xs={12} sx={{ mt: 0.5 }}>
                <Box
                  sx={{
                    width: '100%',
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '10px',
                    bgcolor: '#F8FAFC',
                    border: '1px solid',
                    borderColor: '#EEF2F6',
                  }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="nowrap"
                    sx={{ width: '100%', minWidth: 0 }}>
                    {pageConfig.searchField ? (
                      <TextField
                        size="small"
                        value={searchText}
                        placeholder={pageConfig.searchPlaceholder || 'Search'}
                        onChange={e => setSearchText(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          flex: '1 1 0',
                          minWidth: 0,
                          bgcolor: '#fff',
                          '& .MuiInputBase-root': { minHeight: 36, fontSize: '0.875rem' },
                        }}
                      />
                    ) : null}
                    {pageConfig.headerFilters ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="nowrap"
                        sx={{
                          flex: pageConfig.searchField
                            ? pageConfig.equalSearchFilterSplit
                              ? '1 1 0'
                              : '2 1 0'
                            : '1 1 auto',
                          minWidth: 0,
                          width: pageConfig.searchField ? undefined : '100%',
                          '& > *': { flex: '1 1 0', minWidth: 0 },
                        }}>
                        {pageConfig.headerFilters()}
                      </Stack>
                    ) : null}
                  </Stack>
                </Box>
              </Grid>
              {searchToolbarChips.length > 0 ? (
                <Grid item xs={12} sx={{ mt: 0.75 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {searchToolbarChips.map(chip => (
                      <Chip
                        key={chip.key}
                        size="small"
                        label={chip.label}
                        onDelete={() => handleClearFilter(chip.key)}
                        deleteIcon={<Clear />}
                        sx={filterChipSx}
                      />
                    ))}
                    <Chip
                      size="small"
                      label="Clear all"
                      onClick={clearAllSearchToolbarFilters}
                      onDelete={clearAllSearchToolbarFilters}
                      deleteIcon={<Clear />}
                      sx={filterChipSx}
                    />
                  </Stack>
                </Grid>
              ) : null}
            </>
          ) : (
            <>
          <Grid item xs={screenSizeDownSm && pageConfig.customComponent ? 12 : headerWidth()} sm={6}>
            <Typography sx={pageTitleSxResponsive(screenSizeDownSm)}>
              {pageConfig.title}
            </Typography>
            {pageConfig.subtitle ? (
              <Typography sx={pageSubtitleSx}>
                {pageConfig.subtitle}
              </Typography>
            ) : null}
          </Grid>
          {screenSizeDownSm ? (
            <Grid
              justifyContent={pageConfig.customComponent ? 'space-between' : 'end'}
              item
              xs={pageConfig.customComponent ? 12 : 12 - headerWidth()}
              sm={6}
              textAlign={'right'}
              gap={1}
              container>
              {pageConfig.customComponent && (
                <Grid item xs={12} sx={{ '& > *': { width: '100%' } }}>
                  {pageConfig.customComponent()}
                </Grid>
              )}
              <Grid item sx={{ ml: 'auto' }}>
                <ButtonGroup>
                  {(pageConfig?.importPrivilege || pageConfig?.exportPrivilege) && (
                    <Button onClick={openPopOver}>
                      <MoreVert color="primary" />
                    </Button>
                  )}
                  {showFilterButton && (
                    <Button>
                      <FilterAltIcon onClick={handleFilter} color="primary" />
                    </Button>
                  )}
                  {IsPrivilege?.includes(pageConfig.addPrivilege) && (
                    <Button>
                      <AddIcon
                        color="primary"
                        onClick={() => {
                          pageConfig.customAdd ? pageConfig.customAdd() : Navigate(pageConfig.AddButtonUrl);
                        }}
                      />
                    </Button>
                  )}
                </ButtonGroup>
              </Grid>
            </Grid>
          ) : (
            <Grid justifyContent="end" item xs={12 - headerWidth()} sm={6} textAlign={'right'} gap={1} container>
              <Grid item>{pageConfig.customComponent && pageConfig.customComponent()}</Grid>
              <Grid item>
                {(pageConfig?.importPrivilege || pageConfig?.exportPrivilege) && (
                  <Button
                    variant="outlined"
                    sx={{ textTransform: 'capitalize', mb: 1 }}
                    onClick={openPopOver}
                    endIcon={<KeyboardArrowDown />}>
                    {strings.action_text}
                  </Button>
                )}
              </Grid>
              <Grid item>
                {showFilterButton && (
                  <PrimaryButton
                    variant="outlined"
                    data-testid="filter"
                    onClick={handleFilter}
                    fullWidth={false}
                    sx={{ textTransform: 'capitalize' }}>
                    <FilterAltOutlinedIcon />
                    {mobile ? '' : strings.filter_text}
                  </PrimaryButton>
                )}
              </Grid>
              <Grid>
                {IsPrivilege?.includes(pageConfig.addPrivilege) && (
                  <PrimaryButton
                    variant="outlined"
                    data-testid="add"
                    onClick={() => {
                      pageConfig.customAdd ? pageConfig.customAdd() : Navigate(pageConfig.AddButtonUrl);
                    }}
                    sx={{ width: 'auto', textTransform: 'capitalize' }}>
                    <AddIcon />
                    {mobile ? '' : pageConfig.AddButtonText}
                  </PrimaryButton>
                )}
              </Grid>
            </Grid>
          )}
              {pageConfig.renderKpis ? (
                <Grid item xs={12} sx={{ mt: 0.5 }}>
                  {pageConfig.renderKpis()}
                </Grid>
              ) : null}
            </>
          )}
        </Grid>
        <Popover
          open={openPop}
          anchorEl={anchorEl2}
          onClose={() => {
            setAnchorEl2(null);
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}>
          <Box display={'flex'} flexDirection={'column'} width={'max-content'} sx={{ py: 0.5 }}>
            {pageConfig?.importPrivilege && (
              <Typography
                sx={{
                  cursor: 'pointer',
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  minHeight: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  ':hover': { backgroundColor: `${theme.palette.primary.main}20` },
                }}
                onClick={() => {
                  setAnchorEl2(null);
                  importDrawerOpen();
                }}>
                {`${strings.usermgmt_button_import} ${pageConfig?.title}`}
              </Typography>
            )}
            <Divider sx={{ my: 0 }} />
            {pageConfig?.exportPrivilege && (
              <Typography
                sx={{
                  cursor: 'pointer',
                  px: 1.5,
                  py: 0.75,
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  minHeight: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  ':hover': { backgroundColor: `${theme.palette.primary.main}20` },
                }}
                onClick={() => {
                  setAnchorEl2(null);
                  handleExport();
                }}>
                {`${strings.usermgmt_button_export} ${pageConfig?.title}`}
              </Typography>
            )}
          </Box>
        </Popover>
        <DrawerMenu
          width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
          open={openDrawer}
          handleClose={importDrawerClose}
          closeButton={true}
          title={`${strings.usermgmt_button_import} ${pageConfig?.title}`}>
          <ListPageImportDrawerComponent
            setOpenDrawer={setOpenDrawer}
            isLoading={loading}
            setIsLoading={setLoading}
            setMessage={setMessage}
            downloadTempateApi={pageConfig.downloadImportTemplate}
            importApi={pageConfig.importApi}
          />
        </DrawerMenu>
        {!pageConfig.searchField &&
          filter &&
          pageConfig.filterForm &&
          Object.entries(filter).map(
            ([key, value]: any) =>
              value !== '' &&
              pageConfig.filterForm.find((i: any) => i.field === key)?.label !== undefined && (
                <Chip
                  size="small"
                  key={key}
                  label={`${((): string => {
                    const field = pageConfig.filterForm.find((i: any) => i.field === key);
                    let displayValue: any;
                    switch (field?.type) {
                      case 'drop-down':
                        displayValue = field.options?.find((opt: any) => opt.value === value)?.label ?? value;
                        break;
                      case 'autocomplete':
                      case 'autocompleteFilter':
                        displayValue = value?.name || value;
                        break;
                      default:
                        displayValue = value;
                    }
                    return `${field?.label} : ${displayValue}`;
                  })()}`}
                  onDelete={() => handleClearFilter(key)}
                  deleteIcon={<Clear />}
                  sx={{
                    ...filterChipSx,
                    mr: mobile ? 0.75 : 1.5,
                    my: mobile ? 0.25 : 0.5,
                  }}
                />
              ),
          )}
        {!pageConfig.searchField && filter && Object.values(filter).some((value: any) => value !== '') && (
          <Chip
            size="small"
            label="Clear all"
            onClick={() => applyFilter(true, { preventDefault: () => {} })}
            onDelete={() => applyFilter(true, { preventDefault: () => {} })}
            deleteIcon={<Clear />}
            sx={{
              ...filterChipSx,
              mr: mobile ? 1 : 2,
              my: mobile ? 0.5 : 1,
            }}
          />
        )}
        {showFilterButton && (
          <DrawerMenu
            width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
            open={openFilterDrawer}
            handleClose={importDrawerCloseFilter}
            closeButton={true}
            title={strings.filter_text}>
            {filterDrawerContent}
          </DrawerMenu>
        )}
        {loading || (isLoading && <PageLoader />)}
        {pageConfig.toolbarComponent ? (
          <Box sx={{ width: '100%', mt: 1, display: 'flex' }}>{pageConfig.toolbarComponent()}</Box>
        ) : null}
        {pageConfig.enableRowSelection && selectedIds.length > 0 && pageConfig.selectionToolbar ? (
          <Box sx={{ mt: 2, mb: 0.5 }}>{pageConfig.selectionToolbar(selectedIds, clearSelection)}</Box>
        ) : null}
        {!hideTable && pageResponse?.content?.length > 0 ? (
          <Paper
            sx={{
              width: '100%',
              overflow: 'hidden',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              backgroundColor: theme.palette.background.paper,
              mt: 2.5,
            }}>
            <TableContainer
              sx={{
                boxShadow: 'none',
                overflowX: 'auto',
                overflowY: 'visible',
              }}>
              <Table stickyHeader sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead
                  sx={{
                    '.MuiTableCell-root': {
                      backgroundColor: theme.palette.grey[50],
                      color: theme.palette.text.primary,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      borderTop: 'none',
                      borderLeft: 'none',
                      borderRight: 'none',
                      height: '48px',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      padding: '12px 16px',
                      '.MuiTableSortLabel-root:active, .MuiTableSortLabel-root:hover, .MuiTableSortLabel-root:focus': {
                        color: theme.palette.text.primary,
                      },
                    },
                    '.MuiTableCell-root:first-of-type': {
                      borderTopLeftRadius: theme.shape.borderRadius,
                    },
                    '.MuiTableCell-root:last-of-type': {
                      borderTopRightRadius: theme.shape.borderRadius,
                    },
                  }}>
                  <TableRow>
                    {pageConfig.enableRowSelection ? (
                      <TableCell padding="checkbox" sx={{ width: 48 }}>
                        <Checkbox
                          size="small"
                          indeterminate={somePageSelected && !allPageSelected}
                          checked={allPageSelected}
                          onChange={toggleSelectAllPage}
                          onClick={e => e.stopPropagation()}
                        />
                      </TableCell>
                    ) : null}
                    {pageConfig.tableColumn.map((column: any, index: number) => (
                      <TableCell key={index}>
                        <Stack
                          direction={column.textAlign === 'right' ? 'row-reverse' : 'row'}
                          justifyContent="space-between"
                          alignItems="center">
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <TableSortLabel
                              hideSortIcon={!column?.sortable}
                              active={pageConfig?.sortValue?.sortBy === column?.field}
                              direction={sort ? 'desc' : 'asc'}
                              data-testid={`${'sort' + column.field}`}
                              onClick={() =>
                                column?.sortable ? handleSort(column.localField, column.field, sort) : {}
                              }>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography whiteSpace="nowrap" fontWeight={600} fontSize="14px">
                                  {column.headerName}
                                </Typography>
                              </Stack>
                            </TableSortLabel>
                          </Stack>
                        </Stack>
                      </TableCell>
                    ))}
                    {!pageConfig.hideActionText && (
                      <TableCell
                        sx={{
                          textAlign: 'right',
                        }}>
                        <Typography fontWeight={600} fontSize="14px">
                          {strings.action_text}
                        </Typography>
                      </TableCell>
                    )}
                    {/* } */}
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      padding: '12px 16px',
                      fontSize: '0.875rem',
                      color: theme.palette.text.primary,
                      verticalAlign: 'middle',
                    },
                    '& .MuiTableRow-root': {
                      height: 'auto',
                    },
                    '& .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    },
                  }}>
                  {pageResponse?.content?.length > 0 &&
                    pageResponse?.content?.map((item: any, indexValue: number) => {
                      const firstTextColumnIndex = pageConfig.tableColumn.findIndex((c: any) => !c.image);
                      const isExpanded = Boolean(pageConfig?.isRowExpanded?.(item));
                      const expandedContent = pageConfig?.renderExpandedRow?.(item);
                      const columnCount =
                        pageConfig.tableColumn.length +
                        (!pageConfig.hideActionText ? 1 : 0) +
                        (pageConfig.enableRowSelection ? 1 : 0);
                      return (
                        <Fragment key={item?.id ?? indexValue}>
                        <TableRow
                          onClick={e => {
                            if (
                              (e.target as HTMLElement).closest(
                                'button, [role="button"], a, [style*="cursor: pointer"]',
                              )
                            ) {
                              return;
                            }
                            if (pageConfig?.onRowClick) {
                              pageConfig.onRowClick(item);
                              return;
                            }
                            if (canEdit && (pageConfig?.editButtonUrl || pageConfig?.customEdit) && !item.hideEdit) {
                              navigateToEditRow(item);
                            }
                          }}
                          sx={{
                            cursor:
                              pageConfig?.onRowClick ||
                              (canEdit && (pageConfig?.editButtonUrl || pageConfig?.customEdit) && !item.hideEdit)
                                ? 'pointer'
                                : 'default',
                            bgcolor: isExpanded ? theme.palette.action.selected : 'inherit',
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                            ...(isExpanded
                              ? {
                                  '& > .MuiTableCell-root': {
                                    borderBottom: 0,
                                  },
                                }
                              : {}),
                          }}>
                          {pageConfig.enableRowSelection ? (
                            <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={selectedIds.includes(Number(getRowId(item)))}
                                onChange={() => toggleSelectRow(Number(getRowId(item)))}
                                onClick={e => e.stopPropagation()}
                              />
                            </TableCell>
                          ) : null}
                          {pageConfig.tableColumn.map((_header: any, i: number) => {
                            const isPrimaryNameColumn = i === firstTextColumnIndex;
                            if (_header.image) {
                              return (
                                <TableCell padding="checkbox" key={`${_header.field || 'image'}-${i}`}>
                                  <TableImageCell
                                    imageUrl={item[_header['field']]}
                                    placeholderIcon={_header.imagePlaceholderIcon}
                                    placeholderInitials={
                                      _header.imageInitialsField
                                        ? getInitialsFromName(item[_header.imageInitialsField])
                                        : undefined
                                    }
                                    placeholderName={
                                      _header.imageInitialsField ? item[_header.imageInitialsField] : undefined
                                    }
                                  />
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell
                                sx={{
                                  textAlign: _header['textAlign'] || 'left',
                                  whiteSpace: isWhiteSpace ? 'nowrap' : 'inherit',
                                  ...(isPrimaryNameColumn
                                    ? {
                                        fontWeight: 600,
                                        '& .MuiTypography-root': { fontWeight: 600 },
                                      }
                                    : {}),
                                }}
                                key={_header.field || `col-${i}`}>
                                {_header.renderCell
                                  ? _header.renderCell(item[_header['field']], item)
                                  : item[_header['field']]}
                              </TableCell>
                            );
                          })}
                          {!pageConfig.hideActionText && (
                            <TableCell
                              className="last-row"
                              sx={{
                                textAlign: 'right',
                                div: {
                                  display: 'flex',
                                  justifyContent: 'end',
                                  verticalAlign: 'middle',
                                },
                              }}>
                              <Stack sx={{ alignItems: 'flex-start' }} direction="row" spacing={2}>
                                {(() => {
                                  const rowHasActions =
                                    canToggle ||
                                    (canEdit && !item.hideEdit) ||
                                    (canDelete && !item.hideDelete) ||
                                    hasCustomActions;
                                  if (!rowHasActions) {
                                    return null;
                                  }

                                  // If only one custom action and no other actions, or inlineCustomActions requested,
                                  // show custom icons beside the MoreVert menu when default actions exist.
                                  const hasDefaultRowActions =
                                    canToggle || (canEdit && !item.hideEdit) || (canDelete && !item.hideDelete);
                                  const showInlineCustomActions =
                                    customActions.length > 0 &&
                                    (Boolean(pageConfig.inlineCustomActions) ||
                                      (customActions.length === 1 && !hasDefaultRowActions));

                                  if (showInlineCustomActions && !hasDefaultRowActions) {
                                    const action = customActions[0];
                                    const rowId = item?.id ?? getRowId(item);
                                    return (
                                      <ToolTip title={getCustomActionLabel(action)}>
                                        <IconButton
                                          size="small"
                                          sx={{ p: 0.75 }}
                                          onClick={event => {
                                            event.stopPropagation();
                                            if (rowId !== undefined && rowId !== null) {
                                              action.handleCustomImageMethod(rowId as number, event);
                                            }
                                          }}>
                                          {renderCustomActionIcon(action)}
                                        </IconButton>
                                      </ToolTip>
                                    );
                                  }

                                  return (
                                    <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="flex-end">
                                      {showInlineCustomActions
                                        ? customActions.map((action, actionIdx) => {
                                            const rowId = item?.id ?? getRowId(item);
                                            return (
                                              <ToolTip key={`inline-action-${actionIdx}`} title={getCustomActionLabel(action)}>
                                                <IconButton
                                                  size="small"
                                                  sx={{ p: 0.75 }}
                                                  onClick={event => {
                                                    event.stopPropagation();
                                                    if (rowId !== undefined && rowId !== null) {
                                                      action.handleCustomImageMethod(rowId as number, event);
                                                    }
                                                  }}>
                                                  {renderCustomActionIcon(action)}
                                                </IconButton>
                                              </ToolTip>
                                            );
                                          })
                                        : null}
                                      {hasDefaultRowActions || (!showInlineCustomActions && hasCustomActions) ? (
                                        <ToolTip title={strings.action_text}>
                                          <IconButton
                                            size="small"
                                            sx={{ p: 0.75 }}
                                            onClick={event => {
                                              event.stopPropagation();
                                              handleOpenActionMenu(event, item);
                                            }}>
                                            <MoreVert fontSize="medium" color="primary" />
                                          </IconButton>
                                        </ToolTip>
                                      ) : null}
                                    </Stack>
                                  );
                                })()}
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                        {pageConfig?.renderExpandedRow && isExpanded ? (
                          <TableRow>
                            <TableCell
                              colSpan={columnCount}
                              sx={{
                                p: '0 !important',
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                bgcolor: '#fff',
                                verticalAlign: 'top',
                              }}>
                              {expandedContent}
                            </TableCell>
                          </TableRow>
                        ) : null}
                        </Fragment>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ p: 1.25 }}>
              <Grid item xs={12} sm="auto">
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', pl: { sm: 0.5 } }}>
                  {(() => {
                    const total = pageResponse.totalElements || 0;
                    const label = pageConfig.entityLabel || pageConfig.title || 'records';
                    if (total === 0) return `Showing 0 to 0 of 0 ${label}`;
                    const from = index * pageSize + 1;
                    const to = Math.min((index + 1) * pageSize, total);
                    return `Showing ${from} to ${to} of ${total} ${label}`;
                  })()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm="auto" sx={{ ml: { sm: 'auto' } }}>
                <TablePaginationAction
                  count={pageResponse.totalElements}
                  rowsPerPage={pageSize}
                  page={index}
                  onRowsPerPageChange={(e: SelectChangeEvent<any>) => {
                    pageSizeChange(e);
                  }}
                  onPageChange={handleChangePage}
                />
              </Grid>
            </Grid>
          </Paper>
        ) : (
          !hideTable &&
          !isLoading && (
            <Typography
              sx={{
                p: 2,
                mt: 5,
                borderRadius: 1,
                bgcolor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
              }}
              variant="h6">
              {strings.no_data_available}
            </Typography>
          )
        )}
        {openModal && deleteModal}
        {(canEdit || canDelete || canToggle || hasCustomActions) && (
          <Popover
            open={Boolean(actionMenu.anchorEl)}
            anchorEl={actionMenu.anchorEl}
            onClose={handleCloseActionMenu}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}>
            <MenuList
              sx={{
                minWidth: 200,
                py: 0.5,
                '& .MuiMenuItem-root:not(:last-of-type)': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
              }}>
              {(!pageConfig.inlineCustomActions ? customActions : []).map((action, idx) => (
                <MenuItem
                  key={`custom-action-${idx}`}
                  onClick={event => handleCustomActionClick(action, event)}
                  sx={{
                    minHeight: '30px',
                    py: 0.75,
                    px: 1.5,
                  }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 24,
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    {renderCustomActionIcon(action)}
                  </ListItemIcon>
                  <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}>
                    {getCustomActionLabel(action)}
                  </ListItemText>
                </MenuItem>
              ))}
              {canToggle && (
                <MenuItem
                  onClick={handleToggleRow}
                  sx={{
                    minHeight: '30px',
                    py: 0.75,
                    px: 1.5,
                  }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 24,
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <Box
                      component="img"
                      src={actionMenu.row?.enabled ? images.activeToggleIcon : images.inActiveToggleIcon}
                      alt="toggle user"
                      sx={{ width: 18, height: 18 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      '& .MuiTypography-root': { fontSize: '0.875rem' },
                    }}>
                    {actionMenu.row?.enabled
                      ? strings.usermgmt_button_active_user
                      : strings.usermgmt_button_in_active_user}
                  </ListItemText>
                </MenuItem>
              )}
              {canEdit && (
                <MenuItem
                  onClick={handleEditRow}
                  disabled={Boolean(actionMenu.row?.hideEdit)}
                  sx={{
                    minHeight: '30px',
                    py: 0.75,
                    px: 1.5,
                  }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 24,
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <EditIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}>
                    {strings.editText}
                  </ListItemText>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  onClick={handleDeleteRow}
                  disabled={Boolean(actionMenu.row?.hideDelete)}
                  sx={{
                    minHeight: '30px',
                    py: 0.75,
                    px: 1.5,
                  }}>
                  <ListItemIcon
                    sx={{
                      minWidth: 24,
                      mr: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main', '& .MuiTypography-root': { fontSize: '0.875rem' } }}>
                    {strings.deleteText}
                  </ListItemText>
                </MenuItem>
              )}
            </MenuList>
          </Popover>
        )}
      </Container>
    </>
  );
};
export default ListPage;
